import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Wrench, FileText, Package, CheckCircle2, Wifi, MessageSquare, Activity, Inbox } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, fmtDate } from "../lib/api";
import { PageHeader, StatCard, StatusBadge } from "./kit";

function AdminOverview() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then((r) => setD(r.data));
  }, []);
  if (!d) return null;
  const cards = [["Total customers", d.total_customers, Users], ["Total grinders", d.total_grinders, Wrench], ["Pending quotations", d.pending_quotations, FileText],
    ["Active orders", d.active_orders, Package], ["Completed orders", d.completed_orders, CheckCircle2], ["Online customers", d.online_customers, Wifi],
    ["Online grinders", d.online_grinders, Wifi], ["Unread messages", d.unread_messages, MessageSquare], ["New inquiries", d.new_inquiries, Inbox]];
  return (
    <>
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {cards.map(([l, v, I]) => <StatCard key={l} label={l} value={v} icon={I} testid={`stat-${l.toLowerCase().replace(/\s/g, "-")}`} />)}
      </div>
      <div className="card-neon p-5 mt-6" data-testid="recent-activities">
        <h2 className="font-cond font-bold uppercase tracking-wider text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-[#00d9ff]" /> Recent activity</h2>
        <ul className="mt-3 divide-y divide-white/5">
          {d.activities.map((a) => <li key={a.id} className="py-2.5 flex justify-between gap-4 text-sm"><span className="text-slate-200">{a.text}</span><span className="text-slate-500 whitespace-nowrap">{fmtDate(a.created_at)}</span></li>)}
          {d.activities.length === 0 && <li className="py-4 text-slate-500 text-sm">No activity yet.</li>}
        </ul>
      </div>
    </>
  );
}

function UserOverview({ role }) {
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data));
    if (role === "customer") api.get("/quotations").then((r) => setQuotes(r.data));
  }, [role]);
  const active = orders.filter((o) => !["completed", "cancelled"].includes(o.status));
  return (
    <>
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {role === "customer" && <StatCard label="Quotations" value={quotes.length} icon={FileText} testid="stat-quotations" />}
        {role === "customer" && <StatCard label="Awaiting your reply" value={quotes.filter((q) => q.status === "sent").length} icon={FileText} testid="stat-awaiting" />}
        <StatCard label="Active orders" value={active.length} icon={Package} testid="stat-active-orders" />
        <StatCard label="Completed" value={orders.filter((o) => o.status === "completed").length} icon={CheckCircle2} testid="stat-completed" />
        {role === "grinder" && <StatCard label="Awaiting acceptance" value={orders.filter((o) => !o.grinder_accepted).length} icon={Wrench} testid="stat-awaiting-accept" />}
      </div>
      <div className="card-neon p-5 mt-6">
        <div className="flex items-center justify-between"><h2 className="font-cond font-bold uppercase tracking-wider text-lg">Active orders</h2><Link to="/dashboard/orders" className="text-sm text-[#00d9ff]">View all</Link></div>
        <ul className="mt-3 divide-y divide-white/5">
          {active.map((o) => (
            <li key={o.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
              <div><p className="font-semibold">{o.number} · {o.service}</p><p className="text-xs text-slate-400">Progress {o.progress || 0}%{o.eta ? ` · ETA ${o.eta}` : ""}</p></div>
              <StatusBadge s={o.status} />
            </li>
          ))}
          {active.length === 0 && <li className="py-4 text-slate-500 text-sm">No active orders.</li>}
        </ul>
      </div>
      {role === "customer" && (
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/quote" className="btn-electric" data-testid="overview-new-quote">Request a quote</Link>
          <Link to="/dashboard/messages" className="btn-ghost" data-testid="overview-contact-support">Contact support</Link>
        </div>
      )}
    </>
  );
}

export default function Overview() {
  const { user } = useAuth();
  return (
    <div data-testid="dashboard-overview">
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`} desc={user.role === "admin" ? "Live view of the RushGrind2K business" : "Here's what's happening with your grind"} />
      {user.role === "admin" ? <AdminOverview /> : <UserOverview role={user.role} />}
    </div>
  );
}
